// src/admin/pages/GameCreate.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { crearJuego } from '../../api';
import ImageUploader from '../components/ImageUploader';
import Toast from '../components/Toast';

const tabs = ['IMAGE', 'DETAILS', 'DESCRIPTION'];

const GameCreate = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('IMAGE');
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const [game, setGame] = useState({
    nombre: '',
    descripcion: '',
    duracion: '',
    precio_base: '',
    capacidad_base: '',
    capacidad_maxima: '',
    precio_extra_por_jugador: '',
    imagen_url: '',
    category_id: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGame((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const {
      nombre,
      descripcion,
      duracion,
      precio_base,
      capacidad_base,
      capacidad_maxima,
      precio_extra_por_jugador,
      imagen_url,
      category_id,
    } = game;

    if (!nombre || !descripcion || !duracion || !precio_base || !capacidad_base ||
        !capacidad_maxima || !precio_extra_por_jugador || !imagen_url || !category_id) {
  showToast('warning', 'You must complete all fields.');
      return;
    }

    try {
      await crearJuego(game);
  showToast('success', 'Game created successfully');
      setTimeout(() => navigate('/admin/games'), 1500);
    } catch (error) {
  console.error('Error creating game:', error);
  showToast('error', 'Error creating the game. Please try again later.');
    }
  };

  return (
    <div className="p-8 bg-[rgb(var(--color-bg))] min-h-screen text-[rgb(var(--color-text))] font-inter">
      <h1 className="text-3xl font-audiowide mb-6">Create Game</h1>

      <div className="flex border-b border-[rgb(var(--color-border))] mb-6">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition ${
              activeTab === tab
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-[rgb(var(--color-text))]/70 hover:text-blue-300'
            }`}
          >
            {tab === 'IMAGE' && '📷'}
            {tab === 'DETAILS' && '📐'}
            {tab === 'DESCRIPTION' && '📝'}
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'IMAGE' && (
        <div className="space-y-6 max-w-3xl">
          {game.imagen_url && (
            <div className="relative">
              <label className="block mb-2 text-sm text-[rgb(var(--color-text))]/80">Current image</label>
              <img
                src={game.imagen_url}
                alt="Image"
                className="aspect-video w-full object-cover rounded-xl border border-[rgb(var(--color-border))]"
              />
              <button
                onClick={() => setGame((prev) => ({ ...prev, imagen_url: '' }))}
                className="absolute top-2 right-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-500"
              >
                Remove image
              </button>
            </div>
          )}
          <ImageUploader
            onUpload={(url) => setGame((prev) => ({ ...prev, imagen_url: url }))}
            onReset={() => setGame((prev) => ({ ...prev, imagen_url: '' }))}
          />
        </div>
      )}

      {activeTab === 'DETAILS' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm mb-1">Name</label>
            <input
              name="nombre"
              value={game.nombre}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Category ID</label>
            <input
              name="category_id"
              value={game.category_id}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
        </div>
      )}

      {activeTab === 'DESCRIPTION' && (
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm mb-1">Description</label>
            <textarea
              name="descripcion"
              value={game.descripcion}
              onChange={handleChange}
              className="w-full h-32 p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Duration (min)</label>
            <input
              name="duracion"
              value={game.duracion}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Base price (₡)</label>
            <input
              name="precio_base"
              value={game.precio_base}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Extra price per player (₡)</label>
            <input
              name="precio_extra_por_jugador"
              value={game.precio_extra_por_jugador}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Base capacity</label>
            <input
              name="capacidad_base"
              value={game.capacidad_base}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Maximum capacity</label>
            <input
              name="capacidad_maxima"
              value={game.capacidad_maxima}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
        </div>
      )}

      <div className="flex justify-end mt-10 space-x-3">
        <button
          onClick={() => navigate('/admin/games')}
          className="px-4 py-2 bg-gray-600 rounded hover:bg-gray-500"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-green-600 rounded hover:bg-green-500 text-white"
        >
          Create game
        </button>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default GameCreate;